import React, { useState, useEffect } from 'react';
import { useQuery, gql } from '@apollo/client';
import {
  Activity,
  Server,
  Database,
  Cpu,
  HardDrive,
  MemoryStick,
  Wifi,
  AlertCircle,
  CheckCircle,
  XCircle,
  RefreshCw,
  Clock,
  TrendingUp,
  TrendingDown,
  Zap,
  Globe,
  Shield
} from 'lucide-react';

// GraphQL query for system metrics
const GET_SYSTEM_HEALTH = gql`
  query GetSystemHealth {
    systemHealth {
      status
      uptime
      responseTime
      cpuUsage
      memoryUsage
      diskUsage
      activeConnections
      requestsPerMinute
      errorRate
      databaseStatus
      cacheStatus
      services {
        name
        status
        responseTime
        lastCheck
      }
    }
  }
`;

const AdminSystem = () => {
  const [selectedService, setSelectedService] = useState(null);
  const { loading, error, data, refetch } = useQuery(GET_SYSTEM_HEALTH, {
    pollInterval: 5000 // Refresh every 5 seconds
  });

  // Mock data for demonstration - replace with real data from your monitoring service
  const systemData = data?.systemHealth || {
    status: 'healthy',
    uptime: '15 days, 3 hours',
    responseTime: 234,
    cpuUsage: 45,
    memoryUsage: 62,
    diskUsage: 38,
    activeConnections: 127,
    requestsPerMinute: 450,
    errorRate: 0.02,
    databaseStatus: 'connected',
    cacheStatus: 'active',
    services: [
      { name: 'API Server', status: 'healthy', responseTime: 45, lastCheck: '2 min ago' },
      { name: 'Database', status: 'healthy', responseTime: 12, lastCheck: '2 min ago' },
      { name: 'Redis Cache', status: 'healthy', responseTime: 3, lastCheck: '2 min ago' },
      { name: 'Email Service', status: 'degraded', responseTime: 2500, lastCheck: '2 min ago' },
      { name: 'Payment Gateway', status: 'healthy', responseTime: 350, lastCheck: '2 min ago' }
    ]
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'healthy': return 'green';
      case 'degraded': return 'yellow';
      case 'down': return 'red';
      default: return 'gray';
    }
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'healthy': return <CheckCircle size={16} className="text-green-500" />;
      case 'degraded': return <AlertCircle size={16} className="text-yellow-500" />;
      case 'down': return <XCircle size={16} className="text-red-500" />;
      default: return <AlertCircle size={16} className="text-gray-500" />;
    }
  };

  const getUsageColor = (usage) => {
    if (usage < 50) return 'bg-green-500';
    if (usage < 75) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  const MetricCard = ({ title, value, unit, icon: Icon, trend, status }) => (
    <div className="bg-white rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Icon size={20} className="text-gray-600" />
          <span className="text-sm text-gray-600">{title}</span>
        </div>
        {trend && (
          <div className={`flex items-center text-sm ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend > 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
            <span>{Math.abs(trend)}%</span>
          </div>
        )}
      </div>
      <div className="text-2xl font-bold">
        {value}
        {unit && <span className="text-sm font-normal text-gray-500 ml-1">{unit}</span>}
      </div>
      {status && (
        <div className="mt-2">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full ${getUsageColor(value)}`}
              style={{ width: `${Math.min(value, 100)}%` }}
            />
          </div>
        </div>
      )}
    </div>
  );

  const ServiceRow = ({ service }) => (
    <tr className="hover:bg-gray-50">
      <td className="px-6 py-3">
        <div className="flex items-center gap-2">
          {getStatusIcon(service.status)}
          <span className="font-medium">{service.name}</span>
        </div>
      </td>
      <td className="px-6 py-3">
        <span className={`px-2 py-1 text-xs rounded-full bg-${getStatusColor(service.status)}-100 text-${getStatusColor(service.status)}-800`}>
          {service.status}
        </span>
      </td>
      <td className="px-6 py-3 text-sm">
        {service.responseTime}ms
      </td>
      <td className="px-6 py-3 text-sm text-gray-500">
        {service.lastCheck}
      </td>
    </tr>
  );

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">System Health</h1>
            <p className="text-gray-600">Monitor platform performance and service status</p>
          </div>
          <div className="flex items-center gap-3">
            <div className={`px-3 py-1 rounded-full flex items-center gap-2 bg-${getStatusColor(systemData.status)}-100`}>
              {getStatusIcon(systemData.status)}
              <span className={`text-sm font-medium text-${getStatusColor(systemData.status)}-800`}>
                System {systemData.status}
              </span>
            </div>
            <button
              onClick={() => refetch()}
              className="p-2 hover:bg-gray-100 rounded-lg bg-white"
            >
              <RefreshCw size={20} />
            </button>
          </div>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Uptime</p>
              <p className="text-xl font-bold">{systemData.uptime}</p>
            </div>
            <Clock className="text-green-500" size={24} />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Response Time</p>
              <p className="text-xl font-bold">{systemData.responseTime}ms</p>
            </div>
            <Zap className="text-blue-500" size={24} />
          </div>
        </div>
        <div className="bg-white rounded-lg p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Error Rate</p>
              <p className="text-xl font-bold">{(systemData.errorRate * 100).toFixed(2)}%</p>
            </div>
            <AlertCircle className={systemData.errorRate < 0.01 ? 'text-green-500' : 'text-yellow-500'} size={24} />
          </div>
        </div>
      </div>

      {/* Resource Usage */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <MetricCard
          title="CPU Usage"
          value={systemData.cpuUsage}
          unit="%"
          icon={Cpu}
          trend={-5}
          status={true}
        />
        <MetricCard
          title="Memory"
          value={systemData.memoryUsage}
          unit="%"
          icon={MemoryStick}
          trend={2}
          status={true}
        />
        <MetricCard
          title="Disk Space"
          value={systemData.diskUsage}
          unit="%"
          icon={HardDrive}
          trend={1}
          status={true}
        />
        <MetricCard
          title="Active Connections"
          value={systemData.activeConnections}
          icon={Wifi}
          trend={8}
        />
      </div>

      {/* Services Status */}
      <div className="bg-white rounded-lg shadow-sm mb-6">
        <div className="p-6 border-b">
          <h3 className="text-lg font-semibold">Service Status</h3>
        </div>
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Service</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Response Time</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Check</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {systemData.services.map((service, idx) => (
              <ServiceRow key={idx} service={service} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-semibold mb-4">Request Metrics</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Requests/min</span>
              <span className="font-semibold">{systemData.requestsPerMinute}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Avg Response</span>
              <span className="font-semibold">{systemData.responseTime}ms</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Success Rate</span>
              <span className="font-semibold text-green-600">
                {((1 - systemData.errorRate) * 100).toFixed(2)}%
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg p-6 shadow-sm">
          <h3 className="text-lg font-semibold mb-4">Database Status</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Connection</span>
              <span className="flex items-center gap-1">
                <CheckCircle size={14} className="text-green-500" />
                <span className="font-semibold">{systemData.databaseStatus}</span>
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Cache Status</span>
              <span className="flex items-center gap-1">
                <CheckCircle size={14} className="text-green-500" />
                <span className="font-semibold">{systemData.cacheStatus}</span>
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-gray-600">Query Time</span>
              <span className="font-semibold">12ms avg</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSystem;